import { motion } from "framer-motion";
import { Camera, PenLine, Heart } from "lucide-react";
import { STEPS } from "@/data/content";
import { Reveal, SectionHeading } from "./Reveal";
import { InlineCta } from "./InlineCta";

const SnapArt = () => (
  <div className="relative mx-auto h-full w-[58%] rounded-[1.6rem] border-[6px] border-navy bg-navy shadow-xl transition-transform duration-500 group-hover:-rotate-3">
    <div className="absolute left-1/2 top-1.5 h-1.5 w-10 -translate-x-1/2 rounded-full bg-cream/30" />
    <div className="h-full overflow-hidden rounded-[1.1rem]">
      <img src="/images/drawing.webp" alt="" loading="lazy" className="h-full w-full object-cover" />
    </div>
    <div className="absolute inset-4 rounded-lg border-2 border-dashed border-white/80" />
    <div className="absolute -bottom-3 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-teal text-white ring-4 ring-cream"><Camera className="h-4 w-4" /></div>
  </div>
);

const StoryArt = () => (
  <div className="relative mx-auto h-full w-[78%]">
    <div className="absolute left-0 top-2 h-[78%] w-[62%] overflow-hidden rounded-2xl bg-white p-1.5 shadow-lg -rotate-6 transition-transform duration-500 group-hover:-rotate-[9deg]">
      <img src="/images/clay.webp" alt="" loading="lazy" className="h-full w-full rounded-xl object-cover" />
    </div>
    <div className="absolute bottom-0 right-0 w-[64%] rounded-2xl bg-white p-4 shadow-xl rotate-3 transition-transform duration-500 group-hover:rotate-[5deg]">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-teal-dark">The story</p>
      <p className="mt-1 font-heading text-sm font-extrabold text-navy leading-snug">“Shelly is a very fast turtle.”</p>
      <div className="mt-2.5 space-y-1.5">
        <div className="h-1.5 w-full rounded-full bg-navy/10" />
        <div className="h-1.5 w-2/3 rounded-full bg-navy/10" />
      </div>
      <PenLine className="absolute -right-2 -top-3 h-7 w-7 rounded-full bg-gold p-1.5 text-navy" />
    </div>
  </div>
);

const RevisitArt = () => (
  <div className="relative mx-auto h-full w-[78%]">
    {["/images/ocean.webp", "/images/birthday.webp", "/images/letters.webp"].map((src, i) => (
      <div
        key={src}
        style={{ "--r": `${(i - 1) * 9}deg`, left: `${8 + i * 20}%`, zIndex: i }}
        className="absolute top-3 h-[80%] w-[48%] overflow-hidden rounded-2xl bg-white p-1.5 shadow-lg [transform:rotate(var(--r))] transition-transform duration-500 group-hover:[transform:rotate(calc(var(--r)*1.5))_translateY(-6px)]"
      >
        <img src={src} alt="" loading="lazy" className="h-full w-full rounded-xl object-cover" />
      </div>
    ))}
    <Heart className="absolute -bottom-1 right-2 z-10 h-9 w-9 rounded-full bg-coral p-2 text-white fill-white" />
  </div>
);

const ART = [SnapArt, StoryArt, RevisitArt];

export const HowItWorks = () => (
  <section id="how-it-works" aria-labelledby="how-heading" className="relative px-5 py-24 sm:px-8 lg:py-36">
    <div className="mx-auto max-w-7xl">
      <SectionHeading id="how-heading" eyebrow="How it works" title="From paper piles to precious memories." intro="Three small steps between the fridge door and a collection you'll treasure." />
      <ol className="mt-16 grid gap-6 md:grid-cols-3 lg:mt-20 lg:gap-8">
        {STEPS.map((s, i) => {
          const Art = ART[i];
          return (
            <Reveal as="li" key={s.n} delay={i * 0.12} className="list-none">
              <motion.article
                data-testid={`step-card-${i + 1}`}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="group relative h-full rounded-[2rem] bg-white p-6 shadow-[0_18px_50px_-28px_rgba(18,52,90,0.35)] sm:p-7"
              >
                <div className="relative h-56 rounded-[1.5rem] p-5" style={{ backgroundColor: `${s.accent}22` }}>
                  <Art />
                </div>
                <div className="mt-7 flex items-baseline gap-4">
                  <span className="font-heading text-5xl font-black leading-none" style={{ color: s.accent }}>{s.n}</span>
                  <h3 className="font-heading text-2xl font-extrabold text-navy">{s.title}</h3>
                </div>
                <p className="mt-3 text-base leading-relaxed text-navy/75">{s.text}</p>
              </motion.article>
            </Reveal>
          );
        })}
      </ol>
      <InlineCta source="how-it-works" text="Free to join. Be first in line when KeepThis! opens." />
    </div>
  </section>
);
